import { useState } from "react";
import { Loader2, Lock, MapPin, Navigation, QrCode, ShieldAlert, Timer as TimerIcon, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDur, idem } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Confirm, StatusBadge, Timer, isOverdue } from "@/components/Common";
import { CatMascot, Hold, RouteLine, Tape, Topo } from "@/components/Brand";
import { PaidEventWaiverDialog } from "@/components/Dialogs";
import { Button } from "@/components/ui/button";

const HOLD_COLORS = ["#1A3323", "#3D6F4B", "#5C7F55", "#2A2E2B", "#7C9A6E", "#8BA77D"];
const CARD = "relative overflow-hidden rounded-[26px]";

function Stub({ label, value, testid, sub }) {
  return (
    <div className="flex-1">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#1A3323]/55">{label}</div>
      <div className="font-mono text-3xl font-extrabold text-[#1A3323]" data-testid={testid}>{value}</div>
      {sub && <div className="text-xs text-[#1A3323]/60">{sub}</div>}
    </div>
  );
}

export function WaitingTicket({ t, onChange }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/me/tickets/${t.id}/cancel`);
      toast.success("Tiket dibatalkan.");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const soon = t.ahead <= 5;
  return (
    <div className="pop-in" data-testid="current-ticket-card">
      <div className={cn(CARD, "grain rounded-b-none bg-[#1A3323] px-6 pb-6 pt-5 text-[#FDFCF8]")}>
        <Topo className="absolute -right-20 -top-12 h-56 w-80 text-white/10" />
        <div className="relative flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FDFCF8]/65">Tiket kamu</span>
          <StatusBadge status={t.status} testid="ticket-status-badge" />
        </div>
        <div className="relative mt-1 font-mono text-[76px] font-extrabold leading-none tracking-tight" data-testid="ticket-number"># {t.number}</div>
        <div className="relative mt-2 flex items-center gap-2 text-base font-semibold" data-testid="ticket-route">
          <Hold className="h-5 w-6" color="#B9D3A8" /> {t.route_name} <span className="text-sm font-medium text-[#FDFCF8]/60">· {t.route_type === "LEAD" ? "Lead" : "Top Rope"}</span>
        </div>
        {soon && <Tape className="relative mt-3 bg-[#B9D3A8]">Sebentar lagi naik — siap-siap!</Tape>}
      </div>
      <div className={cn(CARD, "ticket-perf rounded-t-none border-t-2 border-dashed border-[#1A3323]/25 bg-[#E3EDD9] px-6 pb-5 pt-5")}>
        <div className="flex gap-4">
          <Stub label="Posisi" value={`#${t.position}`} sub={`${t.ahead} di depanmu`} testid="ticket-position" />
          <Stub label="Lagi manjat" value={t.now_serving || "—"} testid="ticket-now-serving" />
          <Stub label="Perkiraan" value={`~${t.estimated_wait_minutes}m`} sub="waktu tunggu" testid="ticket-estimate" />
        </div>
        <span className="sr-only" data-testid="ticket-ahead">{t.ahead} orang di depan</span>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-[#1A3323]/55">Perkiraan waktu tunggu, bukan jaminan.</span>
          <Confirm testid="cancel-ticket" title="Batalkan tiket?" desc={`Tiket ${t.number} dibatalkan dan kuotanya dikembalikan.`} destructive confirmText="Batalkan tiket" onConfirm={cancel}
            trigger={<Button variant="ghost" size="sm" disabled={busy} className="h-10 text-[#1A3323]/70" data-testid="cancel-ticket-button"><XCircle className="mr-1 h-4 w-4" /> Batal</Button>} />
        </div>
      </div>
    </div>
  );
}

export const YourTurn = ({ t }) => (
  <div className={cn(CARD, "grain bg-[#1A3323] p-6 text-center text-[#FDFCF8] ring-4 ring-[#B9D3A8] pop-in")} data-testid="current-ticket-card">
    <Topo className="absolute -left-16 -top-10 h-64 w-96 text-white/10" />
    <CatMascot jump className="relative mx-auto -mb-2 w-36" />
    <div className="relative text-4xl font-extrabold tracking-tight" data-testid="ticket-turn-banner">{t.status === "CALLED" ? "GILIRAN KAMU!" : "SIAP-SIAP!"}</div>
    <div className="relative mt-1 font-mono text-7xl font-extrabold leading-none" data-testid="ticket-number"># {t.number}</div>
    <div className="relative mt-3"><StatusBadge status={t.status} testid="ticket-status-badge" /></div>
    <div className="relative mt-3 text-lg font-bold" data-testid="ticket-route">Menuju {t.route_name}</div>
    <p className="relative mx-auto mt-2 max-w-xs text-sm text-[#FDFCF8]/75">
      {t.status === "CALLED" ? "Datangi keeper sekarang. Pakai harness & sepatu, ya!" : "Kehadiran dikonfirmasi. Keeper akan menekan MULAI saat kamu siap naik."}
    </p>
  </div>
);

export const ClimbingNow = ({ t, offset }) => (
  <div className={cn(CARD, "bg-white p-6 text-center ring-2", isOverdue(t, offset) ? "ring-red-400" : "ring-[#1A3323]")} data-testid="current-ticket-card">
    <RouteLine className="absolute left-4 top-4 h-10 w-32 text-[#3D6F4B]/40" />
    <div className="relative flex items-center justify-center gap-2">
      <span className="font-mono text-lg font-extrabold text-[#1A3323]" data-testid="ticket-number">#{t.number}</span>
      <StatusBadge status={t.status} overdue={isOverdue(t, offset)} testid="ticket-status-badge" />
    </div>
    <div className="mt-4"><Timer startedAt={t.started_at} rotationMinutes={t.rotation_minutes} offset={offset} huge /></div>
    <div className="mt-2 text-sm font-extrabold uppercase tracking-[0.25em] text-[#3D6F4B]">Sedang manjat · {t.route_name}</div>
    <p className="mt-3 text-xs text-muted-foreground">Durasi standar {t.rotation_minutes} menit. Keeper yang menandai selesai.</p>
  </div>
);

export const NiceSend = ({ t, eventName, onNext }) => (
  <div className={cn(CARD, "bg-[#E3EDD9] p-6 text-center pop-in")} data-testid="climb-complete-card">
    <Topo className="absolute -right-16 -top-10 h-56 w-80 text-[#1A3323]/10" />
    <CatMascot jump className="relative mx-auto w-32" />
    <div className="relative text-4xl font-extrabold tracking-tight text-[#1A3323]">Ayo Manjat Lagi</div>
    <p className="relative text-sm font-semibold text-[#1A3323]/70">Climb selesai. Mantap!</p>
    <div className="relative mt-4 grid grid-cols-3 gap-2 rounded-2xl bg-white/70 p-3 text-left text-sm">
      <div><div className="text-[11px] text-muted-foreground">Tiket</div><div className="font-mono font-extrabold">{t.number}</div></div>
      <div><div className="text-[11px] text-muted-foreground">Jalur</div><div className="font-bold">{t.route_name}</div></div>
      <div><div className="text-[11px] text-muted-foreground">Durasi</div><div className="font-mono font-extrabold" data-testid="complete-duration"><TimerIcon className="mr-0.5 inline h-3 w-3" />{fmtDur(t.duration_seconds)}</div></div>
    </div>
    <div className="relative mt-2 text-xs text-muted-foreground">{eventName}</div>
    <Button onClick={onNext} className="relative mt-4 h-14 w-full rounded-xl text-base font-extrabold" data-testid="next-ticket-button">Ambil Tiket Lagi</Button>
  </div>
);

export const CheckinSuccess = ({ onNext }) => (
  <div className={cn(CARD, "bg-[#1A3323] p-6 text-center text-[#FDFCF8] pop-in")} data-testid="checkin-success-card">
    <Topo className="absolute -left-10 -top-10 h-56 w-80 text-white/10" />
    <CatMascot jump className="relative mx-auto w-32" />
    <div className="relative text-4xl font-extrabold tracking-tight">KAMU MASUK!</div>
    <p className="relative mt-1 text-sm text-[#FDFCF8]/75">Selamat datang di iyalagi.manjat. Ketemu di wall!</p>
    <Button onClick={onNext} className="relative mt-5 h-14 w-full rounded-xl bg-[#FDFCF8] text-base font-extrabold text-[#1A3323] hover:bg-[#E3EDD9]" data-testid="checkin-success-next-button">Ambil Tiketmu</Button>
  </div>
);

const rupiah = (amount) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(amount || 0);

export function PaidRegistrationCard({
  event,
  profile,
  registration,
  paidWaiverOk,
  eventAccess,
  onDone,
}) {
  const [busy, setBusy] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      await api.post("/me/event-applications", { profile_id: profile.id, event_id: event.id });
      toast.success("Pengajuan dikirim. Selesaikan pembayaran melalui Bank Transfer.");
      onDone();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  const rejected = registration?.status === "REJECTED";
  const pending = registration?.status === "PENDING_PAYMENT" || registration?.status === "APPROVING";
  const approved = registration?.status === "APPROVED";
  const waiverPublished = Boolean(event.paid_waiver);
  const needsWaiver = !paidWaiverOk;
  const needsAction = !approved && (!registration || rejected || needsWaiver);
  const startApplication = () => {
    if (!waiverPublished) {
      toast.error("Waiver khusus event belum diterbitkan oleh admin.");
      return;
    }
    setWaiverOpen(true);
  };
  return (
    <div className={cn(CARD, "bg-white p-6 ring-1 ring-[#1A3323]/10")} data-testid="paid-event-card">
      <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#3D6F4B]"><QrCode className="h-4 w-4" /> Event berbayar</div>
      <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-[#1A3323]">Daftar & bayar dulu</h3>
      <p className="mt-1 text-sm text-muted-foreground">Setelah admin mengonfirmasi pembayaran, kamu bisa langsung ambil tiket tanpa check-in GPS.</p>
      <div className="mt-4 rounded-xl bg-[#E3EDD9] p-4 text-center"><div className="text-xs font-bold uppercase tracking-[0.16em] text-[#1A3323]/60">Biaya event</div><div className="mt-1 font-mono text-3xl font-extrabold text-[#1A3323]" data-testid="paid-event-price">{rupiah(event.price)}</div></div>
      <div className="mt-3 rounded-xl bg-[#F5F3EB] p-4 text-sm text-[#1A3323]" data-testid="paid-event-transfer-info"><div className="font-extrabold">Transfer Pembayaran Event</div><div className="mt-1 whitespace-pre-line">{event.payment_transfer_info || "Informasi transfer belum diatur admin."}</div></div>
      {needsWaiver ? (
        <div
          className="mt-4 rounded-lg bg-amber-50 p-3 text-sm font-semibold text-amber-900"
          data-testid="paid-event-waiver-status"
        >
          {waiverPublished
            ? "Waiver khusus event wajib disetujui sebelum pendaftaran diproses."
            : "Admin belum menerbitkan waiver khusus untuk event ini."}
        </div>
      ) : null}
      {pending && !needsWaiver && (
        <div
          className="mt-4 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-center text-sm font-extrabold text-amber-950"
          data-testid="payment-pending-status"
        >
          Status Menunggu Pembayaran
        </div>
      )}
      {approved && (
        <div
          className="mt-4 rounded-lg bg-green-50 p-3 text-sm font-bold text-green-900"
          data-testid="payment-approved-status"
        >
          Pembayaran dikonfirmasi. Kamu sudah terdaftar di event ini.
          {eventAccess === "UPCOMING"
            ? " Tiket climbing bisa kamu ambil pada hari event."
            : " Tiket climbing siap diambil sekarang."}
        </div>
      )}
      {rejected && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="payment-rejected-status">{registration.rejection_reason || "Pembayaran belum dapat dikonfirmasi."}</p>}
      {needsAction && (
        <Button
          onClick={startApplication}
          disabled={busy || !waiverPublished}
          className="mt-5 h-13 w-full rounded-xl text-base font-extrabold"
          data-testid="apply-paid-event-button"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {needsWaiver
            ? "Baca & Setujui Waiver Khusus"
            : rejected
              ? "Ajukan Ulang"
              : "Sign Up Event"}
        </Button>
      )}
      <PaidEventWaiverDialog
        open={waiverOpen}
        onOpenChange={setWaiverOpen}
        event={event}
        profile={profile}
        onAccepted={apply}
      />
    </div>
  );
}

export function CheckinCard({ event, profile, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const go = () => {
    setError("");
    if (!navigator.geolocation) return setError("GPS tidak tersedia di perangkat ini. Minta bantuan keeper untuk check-in manual.");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/me/checkin", { profile_id: profile.id, event_id: event.id, latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
          onDone(true);
        } catch (e) {
          setError(errMsg(e));
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        setError("Lokasi tidak bisa didapat. Aktifkan izin lokasi, atau minta bantuan keeper untuk check-in manual.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };
  return (
    <div className={cn(CARD, "bg-white p-6 ring-1 ring-[#1A3323]/10 rise")} data-testid="checkin-card">
      <Hold className="absolute -right-3 top-6 h-12 w-14 rotate-12 opacity-90" color="#E3EDD9" />
      <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#3D6F4B]"><MapPin className="h-4 w-4" /> Langkah 1 · Check-in</div>
      <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-[#1A3323]">Sudah sampai venue?</h3>
      <p className="mt-1 text-sm text-muted-foreground">Check-in pakai GPS dalam radius {event.radius_m} m dari {event.location}.</p>
      {event.registration_status !== "OPEN" && <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Check-in sedang ditutup.</p>}
      {error && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="checkin-error">{error}</p>}
      <Button onClick={go} disabled={busy || event.registration_status !== "OPEN"} className="mt-5 h-14 w-full rounded-xl text-base font-extrabold" data-testid="gps-checkin-button">
        {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Navigation className="mr-2 h-5 w-5" />} Check-in Sekarang
      </Button>
    </div>
  );
}

function RouteOption({ r, i, busy, onPick }) {
  const disabled = r.status !== "OPEN" || !r.eligible;
  return (
    <button disabled={disabled || busy} onClick={() => onPick(r)} data-testid={`take-ticket-${r.code}`}
      className={cn("relative flex w-full items-center gap-3 overflow-hidden rounded-[20px] bg-white p-4 text-left ring-1 ring-[#1A3323]/10 transition-transform", disabled ? "opacity-60" : "hover:-translate-y-0.5 active:scale-[.99]")}>
      <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
        <Hold className="absolute inset-0 h-14 w-14" color={disabled ? "#C9C5B6" : HOLD_COLORS[i % HOLD_COLORS.length]} />
        <span className="relative font-mono text-xl font-extrabold text-[#FDFCF8]">{r.code}</span>
      </div>
      <div className="min-w-0 flex-1" data-testid={`route-option-${r.code}`}>
        <div className="font-extrabold text-[#1A3323]">{r.name}</div>
        <div className="mt-0.5 flex flex-wrap gap-1"><span className="whitespace-nowrap rounded bg-[#E3EDD9] px-1.5 py-0.5 text-[10px] font-bold text-[#1A3323]">{r.route_type === "LEAD" ? "LEAD" : "TOP ROPE"}</span>{r.grade && <span className="whitespace-nowrap rounded bg-[#1A3323] px-1.5 py-0.5 font-mono text-[10px] font-bold text-[#FDFCF8]" data-testid={`route-grade-${r.code}`}>{r.grade}</span>}</div>
        <div className="text-xs text-muted-foreground">{r.status !== "OPEN" ? "Jalur ditutup" : `${r.waiting_count} antre${r.current_number ? ` · lagi manjat ${r.current_number}` : ""}`}</div>
        {r.lead_required && !r.eligible && <div className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-[#2A2E2B]"><ShieldAlert className="h-3 w-3" /> Perlu verifikasi Lead</div>}
      </div>
      <span className={cn("rounded-xl px-3 py-2 text-sm font-extrabold", disabled ? "bg-stone-200 text-stone-500" : "bg-[#1A3323] text-[#FDFCF8]")}>
        {disabled ? <Lock className="h-4 w-4" /> : "Ambil"}
      </span>
    </button>
  );
}

export function RoutePicker({ routes, profile, event, lastTicket, onDone }) {
  const [busy, setBusy] = useState(false);
  const pick = async (r) => {
    setBusy(true);
    try {
      await api.post("/me/tickets", { profile_id: profile.id, event_id: event.id, route_id: r.id },
        { headers: { "Idempotency-Key": idem("tk", profile.id, r.id, lastTicket?.id || "first") } });
      toast.success(`Tiket ${r.name} sudah di tangan!`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      onDone();
    }
  };
  return (
    <section className="space-y-2.5 rise" data-testid="route-picker">
      <div className="flex items-end justify-between px-1">
        <div>
          <div className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#3D6F4B]">Langkah 2 · Pilih jalur</div>
          <h3 className="text-2xl font-extrabold tracking-tight text-[#1A3323]">Mau manjat di mana?</h3>
        </div>
        {lastTicket && <div className="flex items-center gap-1 text-xs" data-testid="last-ticket-info"><span className="font-mono font-bold">{lastTicket.number}</span><StatusBadge status={lastTicket.status} /></div>}
      </div>
      <p className="px-1 text-xs text-muted-foreground">Satu tiket = satu giliran manjat.</p>
      {routes.map((r, i) => <RouteOption key={r.id} r={r} i={i} busy={busy} onPick={pick} />)}
    </section>
  );
}
